import {  useEffect, useState } from "react";
import styles from "./StudyPrograms.module.scss";
import { Button, Checkbox, Dropdown, MenuProps, Modal, Pagination, Popover, Progress, Select, Table, Tag, Tooltip, Typography, Upload } from "antd";
import Search from 'antd/lib/input/Search';
import { useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import Layout from "../../Containers/Layout";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DownloadOutlined, InboxOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Link, useHistory, useParams  } from "react-router-dom";
import { theme } from "../../theme";
import Dragger from "antd/lib/upload/Dragger";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { isProgramSupervisor, useIsAdmin, useIsDean, useIsDirectorCsud, useCurrentUserFacultyId } from "../../utils/utilFunctions";
import { NOTIFICATION_TYPES, openNotification } from "../Notifications/NotificationsUtils";
import type { ColumnsType } from 'antd/es/table';
import { deleteStudyProgram, getDuplicateDisciplinesReport, getFilterOptions, getLearningOutcomesReport, getMissingTitularsReport, getNonApprovedSubjectsReport, getStudyPrograms } from "../../Requests/study-program-request";
import { getDomains } from "../../Requests/domain-requests";
import { getFaculties } from "../../Requests/faculty-requests";
import { AccreditationTypeEnum, MasterCategoryEnum, StudyCycleEnum, StudyPlanStatusEnum, StudyProgramLangEnum } from "../../Api";
import * as _ from 'lodash';
import { downloadExcel, downloadExcelWorkbook } from "../../utils/downloadUtils";
import { getUniversities } from "../../Requests/university-requests";
import { importFromExcel } from "../../Requests/excel-import-requests";
import { mapAccreditationTypeShortToString, mapAccreditationTypeToString, mapStudyProgramLangToString } from "../Subject/helpers";
import { showImportError } from "../../utils/showImportError";
import { getAllStudyPlans, getStudyPlanById } from "../../Requests/study-plan-requests";
import useColumnVisibility from "../../Hooks/useColumnVisibility";
const { Option } = Select;

const toFilterArray = <T,>(arr: T[] | null | undefined): T[] | null => (arr && arr.length > 0 ? arr : null);

interface PageResponse<T> {
    data?: T[] | null,
    totalCount?: number,
    page?: number,
    pageSize?: number
}

export enum ActionKeyEnum {
    EditPlan,
    Edit,
    ImportPlan,
    Delete,
    ExternalEvaluation,
    AdminBoardDecision,
    SenateDecision,
    SustainabilityAnalysis,
    StakeholderConsultationProof,
    RncisForm,
    RncisCertificate,
    DiplomaSupplement,
    InternalEvaluationReport,
    ProgressReport,
    OtherRelevantDocuments,
    AncUFile,
    AncDFile,
    AracisUFile,
    AracisDFile,
    LearningCompetences,
}

export type PageCallBack<T> = (currentPage: number, pageSize: number) => Promise<PageResponse<T> | null | undefined>;
export type ColumnType<T> = { title: string, key: any, dataIndex?: string, width?: any, render?: ((text: string, record: T, index: number) => any) | ((date: Date) => any) };

const StudyPrograms = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const isAdmin = useIsAdmin();
    const isDean = useIsDean();
    const currentUserFacultyId = useCurrentUserFacultyId();
    // Decanul are actiuni doar pe programele facultatii lui
    const isDeanOfProgram = (record: { facultyId?: string }) => !!isDean && !!record.facultyId && record.facultyId === currentUserFacultyId;
    const isDirectorCsud = useIsDirectorCsud();
    const { studyCycle } = useParams<{ studyCycle: StudyCycleEnum }>();
    const isDoctorate = studyCycle === StudyCycleEnum.Doctorate;
    const canManageDoctorate = isAdmin || (isDirectorCsud && isDoctorate);

    
    const [filters, setFilters] = useState<{searchTermName: string, searchTermDomain: string[], searchTermFaculty: string[], 
        searchTermUniversity: string, pageSize: number, currentPageBachelor: number, currentPageMaster : number,
        currentPageDoctorate: number,
        programLanguageOptions: StudyProgramLangEnum[], ectsOptions: number[], externalEvaluationOptions: string[],
        accreditationTypeOptions: AccreditationTypeEnum[], capacityOptions: number[], masterCategoryOptions: MasterCategoryEnum[],
        ancNoticeDateOptions: string[], ancExpirationDateOptions: string[], aracisExpirationDateOptions: string[], ocuppationSearchTerm: string, codCorSearchTerm: string,
        eurAceCertificationFilter: 'all' | 'yes' | 'no'
        }>(() => {
            const savedFilters = localStorage.getItem('studyProgramsFilters');
            const asArray = (v: any) => Array.isArray(v) ? v : [];
            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);
                return {
                    ...parsed,
                    searchTermDomain: asArray(parsed.searchTermDomain),
                    searchTermFaculty: asArray(parsed.searchTermFaculty),
                    programLanguageOptions: asArray(parsed.programLanguageOptions),
                    ectsOptions: asArray(parsed.ectsOptions),
                    externalEvaluationOptions: asArray(parsed.externalEvaluationOptions),
                    accreditationTypeOptions: asArray(parsed.accreditationTypeOptions),
                    capacityOptions: asArray(parsed.capacityOptions),
                    masterCategoryOptions: asArray(parsed.masterCategoryOptions),
                    ancNoticeDateOptions: asArray(parsed.ancNoticeDateOptions),
                    ancExpirationDateOptions: asArray(parsed.ancExpirationDateOptions),
                    aracisExpirationDateOptions: asArray(parsed.aracisExpirationDateOptions),
                    eurAceCertificationFilter: parsed.eurAceCertificationFilter ?? 'all',
                };
            }
            return {
            searchTermName: '', 
            searchTermDomain: [], 
            searchTermFaculty: [], 
            searchTermUniversity: '00000000-0000-0000-0000-000000000000',
            pageSize: 10,
            currentPageBachelor: 1,
            currentPageMaster: 1,
            currentPageDoctorate: 1,
            programLanguageOptions: [],
            ectsOptions: [],
            externalEvaluationOptions: [],
            accreditationTypeOptions: [],
            capacityOptions: [],
            masterCategoryOptions: [],
            ancNoticeDateOptions: [],
            ancExpirationDateOptions: [],
            aracisExpirationDateOptions: [],
            ocuppationSearchTerm: '',
            codCorSearchTerm: '',
            eurAceCertificationFilter: 'all'
            };
    })
    const [currentPage, setCurrentPage] = useState(() => {
        return studyCycle === StudyCycleEnum.Bachelor
            ? filters.currentPageBachelor
            : studyCycle === StudyCycleEnum.Master
                ? filters.currentPageMaster
                : filters.currentPageDoctorate;
    })
    const [uploading, setUploading] = useState(false);
    const [columnVisibility, setColumnVisibility] = useColumnVisibility('studyProgramsColumnVisibility_' + studyCycle);
    useEffect(() => {
        localStorage.setItem('studyProgramsFilters', JSON.stringify(filters));
    }, [filters]);

    const [modalProps, setModalProps] = useState<{isModalVisible: boolean, title: string, fileExtension: string, studyProgramId: string, uploadHandler: any}>({
        isModalVisible: false,
        title: '',
        fileExtension: '',
        studyProgramId: '',
        uploadHandler: undefined,
    });

    const upload = async (file: Blob | null) => {
        if (file) {
            await modalProps.uploadHandler(modalProps.studyProgramId, file).then(uploadSucceded);
        }
    };

    useEffect(() => {
        localStorage.setItem('studyProgramsFilters', JSON.stringify(filters));
    }, [filters]);

    const setPageFilters = (page: number, pageSize: number) => { 
        if (studyCycle === StudyCycleEnum.Bachelor)
        {
            setFilters({...filters, currentPageBachelor : page, pageSize : pageSize})
        }
        else if (studyCycle === StudyCycleEnum.Master)
        {
            setFilters({...filters, currentPageMaster : page, pageSize : pageSize})
        }
        else
        {
            setFilters({...filters, currentPageDoctorate : page, pageSize : pageSize})
        }

    }

    const getActions = (record: any): ItemType[] | undefined => {
        let actions : MenuProps['items'] = [];
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors) || isDeanOfProgram(record)) && actions.push(
        {
            label: isDoctorate ? 'Editare domeniu' : 'Editare program',
            key: ActionKeyEnum.Edit,
            icon:
            <Tooltip title={isDoctorate ? 'Editare domeniu' : 'Editare program'}>
                <FontAwesomeIcon icon={solid('edit')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        isAdmin && !isDoctorate && actions.push(
        {
            label: 'Import plan de învățământ',
            key: ActionKeyEnum.ImportPlan,
            icon:
            <Tooltip title='Import plan de învățământ'>
                <FontAwesomeIcon icon={solid('file-import')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        actions.push(
        {
            label: 'Vizualizare plan de învățământ',
            key: ActionKeyEnum.EditPlan,
            icon:
            <Tooltip title='Vizualizare plan de învățământ'>
                <FontAwesomeIcon icon={solid('edit')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        (canManageDoctorate || isProgramSupervisor(record.programSupervisors) || isDeanOfProgram(record)) && actions.push(
        {
            label: 'Corelarea competențelor cu rezultatele învățării',
            key: ActionKeyEnum.LearningCompetences,
            icon:
            <Tooltip title='Corelarea competențelor cu rezultatele învățării'>
                <FontAwesomeIcon icon={solid('graduation-cap')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        canManageDoctorate && actions.push(
        {
            label: isDoctorate ? 'Ștergere domeniu' : 'Ștergere program',
            key: ActionKeyEnum.Delete,
            icon:
            <Tooltip title={isDoctorate ? 'Ștergere domeniu' : 'Ștergere program'}>
                <FontAwesomeIcon icon={solid('trash')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Raport de evaluare externă',
            key: ActionKeyEnum.ExternalEvaluation,
            icon:
            <Tooltip title='Raport de evaluare externă'>
                <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });

        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Hotărâri CA',
            key: ActionKeyEnum.AdminBoardDecision,
            icon:
            <Tooltip title='Hotărâri CA'>
                <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Hotărâri Senat',
            key: ActionKeyEnum.SenateDecision,
            icon:
            <Tooltip title='Hotărâri Senat'>
                <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
            </Tooltip>,
        });
        
        (isAdmin || isProgramSupervisor(record.programSupervisors)) && !isDoctorate && actions.push(
        {
            label: 'Analiză de sustenabilitate',
            key: ActionKeyEnum.SustainabilityAnalysis,
            icon: (
                <Tooltip title="Analiză de sustenabilitate">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Dovadă consultare părți',
            key: ActionKeyEnum.StakeholderConsultationProof,
            icon: (
                <Tooltip title="Dovadă consultare părți interesate">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (isAdmin || isProgramSupervisor(record.programSupervisors)) && !isDoctorate && actions.push(
        {
            label: 'Formular RNCIS',
            key: ActionKeyEnum.RncisForm,
            icon: (
                <Tooltip title="Formular RNCIS">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (isAdmin || isProgramSupervisor(record.programSupervisors)) && !isDoctorate && actions.push(
        {
            label: 'Certificat RNCIS',
            key: ActionKeyEnum.RncisCertificate,
            icon: (
                <Tooltip title="Certificat RNCIS">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Supliment diplomă',
            key: ActionKeyEnum.DiplomaSupplement,
            icon: (
                <Tooltip title="Supliment diplomă">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Raport de evaluare internă',
            key: ActionKeyEnum.InternalEvaluationReport,
            icon: (
                <Tooltip title="Raport de evaluare internă">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Raport de progres',
            key: ActionKeyEnum.ProgressReport,
            icon: (
                <Tooltip title="Raport de progres">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        
        (canManageDoctorate || isProgramSupervisor(record.programSupervisors)) && actions.push(
        {
            label: 'Alte documente relevante',
            key: ActionKeyEnum.OtherRelevantDocuments,
            icon: (
                <Tooltip title="Alte documente relevante">
                    <FontAwesomeIcon icon={solid('circle-check')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px" }}/>
                </Tooltip>
            ),
        });
        return actions;
    }
    const [modalPropsDelete, setModalPropsDelete] = useState({
        isModalVisible: false,
        title: '',
        fileExtension: '',
        studyProgramId: '',
        //uploadHandler: uploadCVs
    });

    const [modalPropsStatus, setModalPropsStatus] =
    useState<{  isModalVisible: boolean,
                title: string,
                proposal: any | undefined
            }>
    ({
        isModalVisible: false,
        title: '',
        proposal: undefined,
    });

    const handleShow = (studyProgramId: string, uploadHandler: any, fileExtension: string = 'pdf') => {
        setModalProps(modalProps => ({...modalProps, isModalVisible: true, studyProgramId, uploadHandler, fileExtension}));
    }

    const handleClose = () => {
        console.log('aocolasd');
        setModalProps(modalProps => ({...modalProps, isModalVisible: false}));
    }

    const handleOk = () => {
        console.log('aoco');
        setModalProps(modalProps => ({...modalProps, isModalVisible: false}));

    }

    const handleShowDelete = (studyProgramId: string) => {
        setModalPropsDelete(modalProps => ({...modalProps, isModalVisible: true, studyProgramId}));
    }

    const handleCloseDelete = () => {
        setModalPropsDelete(modalProps => ({...modalProps, isModalVisible: false}));
    }

    const handleOkDelete = () => {
        setModalPropsDelete(modalProps => ({...modalProps, isModalVisible: false}));
        deleteStudyProgram((modalPropsDelete.studyProgramId))
                .then(() => {
                    deleteSucceded();
                    invalidateQuery();
                })
                .catch(deleteFailed);

    }

    const handleShowStatus = (proposal: any) => {
        setModalPropsStatus(modalPropsStatus => ({...modalPropsStatus, isModalVisible: true, proposal}));
    }

    const handleCloseStatus = () => {
        setModalPropsStatus(modalPropsStatus => ({...modalPropsStatus, isModalVisible: false}));
    }

    const handleTablePageChange = (page: number, pageSize: number) => {
        setPageFilters(page, pageSize);
        setCurrentPage(page);
    };

    const uploadFailed = (_err: any) => {
        openNotification(
            'Eroare',
            'Încarcarea nu s-a realizat cu succes!',
            NOTIFICATION_TYPES.ERROR
        );
    };


    const uploadSucceded = () => {
        handleClose();
        openNotification(
            'Succes',
            'Încarcarea s-a realizat cu succes!',
            NOTIFICATION_TYPES.SUCCESS
            );
        };

    const validationSucceded = () => {
        openNotification(
            'Succes',
            'Validarea s-a realizat cu succes!',
            NOTIFICATION_TYPES.SUCCESS
        );
    };

    const validationFailed = (_err: any) => {
        openNotification(
            'Eroare',
            'Validarea nu s-a realizat cu succes!',
            NOTIFICATION_TYPES.ERROR
        );
    };

    const deleteSucceded = () => {
        openNotification(
            'Succes',
            'Ștergerea s-a realizat cu succes!',
            NOTIFICATION_TYPES.SUCCESS
        );
    };

    const deleteFailed = (_err: any) => {
        openNotification(
            'Eroare',
            'Ștergerea nu s-a realizat cu succes!',
            NOTIFICATION_TYPES.ERROR
        );
    };

    const wrongFile = () => {
        openNotification(
            'Eroare',
            'Extensia fișierului selectat nu este permisă!',
            NOTIFICATION_TYPES.ERROR
        );
    };


    // const upload = async (file: Blob | null) => {
    //     if (file) {            
    //         await modalProps.uploadHandler(modalProps.studyProgramId, file).then(uploadSucceded);

    //     }
    // }

    const queryClient = useQueryClient();

    const invalidateQuery = () => { queryClient.invalidateQueries('getStudyProgramPage') };
    const mapEurAceFilterToNullableBool = (value: 'all' | 'yes' | 'no'): boolean | null =>
        value === 'all' ? null : value === 'yes';

    const buildReportQueryParams = (page?: number, pageSize?: number) => ({
        ...filters,
        namePattern: filters.searchTermName,
        studyCycle: studyCycle,
        page: page ?? 1,
        pageSize: pageSize ?? 100,
        universityCenterId: filters.searchTermUniversity,
        domainIds: toFilterArray(filters.searchTermDomain),
        facultyIds: toFilterArray(filters.searchTermFaculty),
        ocuppationSearchTerm: filters.ocuppationSearchTerm,
        codCorSearchTerm: filters.codCorSearchTerm,
        studyProgramLanguageOptions: toFilterArray(filters.programLanguageOptions),
        ectsOptions: toFilterArray(filters.ectsOptions),
        externalEvaluationOptions: toFilterArray(filters.externalEvaluationOptions),
        accreditationTypeOptions: toFilterArray(filters.accreditationTypeOptions),
        isEurAceCertified: mapEurAceFilterToNullableBool(filters.eurAceCertificationFilter),
        capacityOptions: toFilterArray(filters.capacityOptions),
        masterCategoryOptions: toFilterArray(filters.masterCategoryOptions),
        ancNoticeDateOptions: toFilterArray(filters.ancNoticeDateOptions),
        ancExpirationDateOptions: toFilterArray(filters.ancExpirationDateOptions),
        aracisExpirationDateOptions: toFilterArray(filters.aracisExpirationDateOptions),
    } as any);

    const getStudyCycleLabel = () => {
        if (studyCycle === StudyCycleEnum.Bachelor) return 'Licență';
        if (studyCycle === StudyCycleEnum.Doctorate) return 'Doctorat';
        return 'Masterat';
    };

    const openFetchError = (_error: any) => {
        openNotification(
            'Eroare!',
            'Datele nu au putut fi aduse din server!',
            NOTIFICATION_TYPES.ERROR
        );
    }

    const { data, isLoading } = useQuery(['getStudyProgramPage', filters, filters.pageSize, filters.currentPageBachelor, filters.currentPageMaster, studyCycle],
        () => {
            return getStudyPrograms({...filters, namePattern: filters.searchTermName, page: studyCycle == StudyCycleEnum.Bachelor ? filters.currentPageBachelor : filters.currentPageMaster, studyCycle: studyCycle,
                                     universityCenterId: filters.searchTermUniversity,
                                     domainIds: toFilterArray(filters.searchTermDomain),
                                     facultyIds: toFilterArray(filters.searchTermFaculty),
                                     ocuppationSearchTerm: filters.ocuppationSearchTerm,
                                     codCorSearchTerm: filters.codCorSearchTerm,
                                     studyProgramLanguageOptions: toFilterArray(filters.programLanguageOptions),
                                     ectsOptions: toFilterArray(filters.ectsOptions),
                                     externalEvaluationOptions: toFilterArray(filters.externalEvaluationOptions),
                                     accreditationTypeOptions: toFilterArray(filters.accreditationTypeOptions),
                                     isEurAceCertified: mapEurAceFilterToNullableBool(filters.eurAceCertificationFilter),
                                     capacityOptions: toFilterArray(filters.capacityOptions),
                                     masterCategoryOptions: toFilterArray(filters.masterCategoryOptions),
                                     ancNoticeDateOptions: toFilterArray(filters.ancNoticeDateOptions),
                                     ancExpirationDateOptions: toFilterArray(filters.ancExpirationDateOptions),
                                     aracisExpirationDateOptions: toFilterArray(filters.aracisExpirationDateOptions)} as any);
        },
        {
            onError: openFetchError,
        }
    );

    const validateLearningCorrelationAccess = async (programId: string) => {
        try {
            const plans = await getAllStudyPlans(programId, 1, 100);
            const draftPlans = plans?.data?.filter((p: any) => p.status === StudyPlanStatusEnum.Draft) ?? [];

            if (!draftPlans.length) {
                openNotification(
                    'Eroare!',
                    'Nu există planuri cadru draft disponibile pentru verificarea corelărilor.',
                    NOTIFICATION_TYPES.ERROR
                );
                return false;
            }

            const latestDraftPlan = [...draftPlans].sort((a, b) => (b.planYear ?? 0) - (a.planYear ?? 0))[0];
            if (!latestDraftPlan?.id) {
                openNotification(
                    'Eroare!',
                    'Nu s-a putut identifica planul cadru draft pentru verificare.',
                    NOTIFICATION_TYPES.ERROR
                );
                return false;
            }
            const studyPlan = await getStudyPlanById(latestDraftPlan.id);

            const competencesCount =
                (studyPlan.programProfessionalCompetences?.length ?? 0) +
                (studyPlan.programTransversalCompetences?.length ?? 0);
            const learningResultsCount =
                (studyPlan.learningKnowledgeOutcome?.length ?? 0) +
                (studyPlan.learningSkillOutcome?.length ?? 0) +
                (studyPlan.learningResponsabilityOutcome?.length ?? 0);

            // Aliniat cu backend-ul (StudyPlanController.ValidateStudyPlanCompetencesAsync): master/doctorat minim 2, licență minim 4
            const minResultsPerCompetence = studyCycle === StudyCycleEnum.Bachelor ? 4 : 2;
            const minResults = minResultsPerCompetence * competencesCount;
            const maxResults = 10 * competencesCount;

            if (learningResultsCount < minResults || learningResultsCount > maxResults) {
                openNotification(
                    'Eroare!',
                    `Nu puteți accesa corelarea competențelor cu rezultatele învățării. Numărul rezultatelor (${learningResultsCount}) trebuie să fie în intervalul [${minResults}, ${maxResults}] pentru ${competencesCount} competențe.`,
                    NOTIFICATION_TYPES.ERROR
                );
                return false;
            }

            return true;
        } catch {
            openNotification(
                'Eroare!',
                'Nu s-a putut valida condiția pentru accesarea corelării competențelor.',
                NOTIFICATION_TYPES.ERROR
            );
            return false;
        }
    };

    const onMenuClick = async (e: any, record: any) => {
        if (e.key == ActionKeyEnum.Edit)
        {
            history.push('/' + studyCycle + '/editare-program/' + record.id);
            return;
        }
        if (e.key == ActionKeyEnum.EditPlan)
        {
            history.push('/' + studyCycle + '/plan/' + record.id);
            return;
        }
        if (e.key == ActionKeyEnum.ImportPlan)
        {
            setModalProps(
                {...modalProps, 
                    isModalVisible: true, 
                    studyProgramId: record.id, 
                    uploadHandler: importFromExcel, 
                    fileExtension: 'xlsx'});
        }
        else if (e.key == ActionKeyEnum.LearningCompetences) {
            const canNavigate = await validateLearningCorrelationAccess(record.id);
            if (canNavigate) {
                history.push(`/${studyCycle}/learning-competences/${record.id}`);
            }
            return;
        }
        else if (e.key == ActionKeyEnum.Delete)
        {
            handleShowDelete(record.id!);
            return;
        }
        else if (e.key == ActionKeyEnum.ExternalEvaluation)
        {
            history.push('/' + studyCycle + '/ExternalEvaluation/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.AdminBoardDecision)
        {
            history.push('/' + studyCycle + '/AdminBoardDecision/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.SenateDecision)
        {
            history.push('/' + studyCycle + '/SenateDecision/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.SustainabilityAnalysis)
        {
            history.push('/' + studyCycle + '/SustainabilityAnalysis/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.StakeholderConsultationProof)
        {
            history.push('/' + studyCycle + '/StakeholderConsultationProof/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.RncisForm)
        {
            history.push('/' + studyCycle + '/RNCISForm/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.RncisCertificate)
        {
            history.push('/' + studyCycle + '/RNCISCertificate/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.DiplomaSupplement)
        {
            history.push('/' + studyCycle + '/DiplomaSupplement/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.InternalEvaluationReport)
        {
            history.push('/' + studyCycle + '/InternalEvaluationReport/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.ProgressReport)
        {
            history.push('/' + studyCycle + '/ProgressReport/' + record.id);
            return;
        }
        else if (e.key == ActionKeyEnum.OtherRelevantDocuments)
        {
            history.push('/' + studyCycle + '/OtherRelevantDocuments/' + record.id);
            return;
        }
        
    };

    const FilterByNameInput = (
        <div>
            <label>Nume</label>
            <Search
                placeholder={t('account.name')}
                defaultValue={filters.searchTermName}
                allowClear
                onSearch={(searchTermName) => {
                    setFilters({
                    ...filters,
                    searchTermName: searchTermName,
                    currentPageBachelor: 1,
                    currentPageMaster: 1
                    });
                }}
                style={{ width: 270 }}
                />
        </div>
    );

    const FilterByOcuppationInput = (
        <div>
            <label>Denumire ocupatie</label>
            <Search
                placeholder={'Denumire ocupatie'}
                defaultValue={filters.ocuppationSearchTerm}
                allowClear
                onSearch={(searchTermOcuppation) => {
                    setFilters({
                    ...filters,
                    ocuppationSearchTerm: searchTermOcuppation,
                    currentPageBachelor: 1,
                    currentPageMaster: 1
                    });
                }}
                style={{ width: 270 }}
                />
        </div>
    );

    const FilterByCodCorInput = (
        <div>
            <label>Cod COR/ESCO</label>
            <Search
                placeholder="Cod COR/ESCO"
                defaultValue={filters.codCorSearchTerm}
                allowClear
                onSearch={(searchTermCodCor) => {
                    setFilters({
                    ...filters,
                    codCorSearchTerm: searchTermCodCor,
                    currentPageBachelor: 1,
                    currentPageMaster: 1
                    });
                }}
                style={{ width: 220 }}
                />
        </div>
    );

    const { data: filterOptions } = useQuery(['getOptions', studyCycle],
        () => {
            return getFilterOptions(studyCycle);
        },
        {
            onError: openFetchError,
        }
    );

    const { data: domains } = useQuery(['getDomains', studyCycle],
        () => {
            return getDomains(undefined, undefined, studyCycle, 1, 100);
        },
        {
            onError: openFetchError,
        }
    );
      
    const { data: universities } = useQuery(['getUniversities'],
        () => {
            return getUniversities();
        },
        {
            onError: openFetchError,
        }
    );

    const { data: faculties } = useQuery(['getFaculties', filters.searchTermUniversity],
        () => {
            return getFaculties(undefined, filters.searchTermUniversity, 1, 50);
        },
        {
            onError: openFetchError,
        }
    );

    const getOptions = () => {
        let options: any = [];

        let groups = _.groupBy(domains?.data, 'branchName');
        
        for (const [key, value] of Object.entries(groups)) {
            options.push({
                label: key,
                options: value.map((option: any) => (
                    {label: option.name, value: option.id}
                ))
            });
        }
        return options;
    }


    const FilterByDomainInput = (
        <div>
            <label>Domeniu</label>
            <Select
                mode="multiple"
                allowClear
                showSearch
                maxTagCount={1}
                maxTagTextLength={30}
                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                filterOption={(input, option) => {
                    const value = (option as { value?: string })?.value;
                    if (value === undefined || value === null) {
                        return false;
                    }
                    const label = String((option as { label?: string })?.label ?? '');
                    return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filters.searchTermDomain}
                style={{ width: "370px" }} 
                placeholder="Toate"
                onChange={(searchTermDomain) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, searchTermDomain: searchTermDomain || []})}}
                options={getOptions()}>
            </Select>
        </div>
    );

    const getLanguageOptions = () => {
        let options: any = [];

        let groups = filterOptions?.studyProgramLanguageOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: mapStudyProgramLangToString(value),
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByLanguageInput = (
        <div>
            <label>Limba de predare</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.programLanguageOptions ?? []} style={{ width: "180px" }} 
                onChange={(programLanguageOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, programLanguageOptions: programLanguageOptions})}}
                options={getLanguageOptions()}>
            </Select>
        </div>
    );

    const getEctsOptions = () => {
        let options: any = [];

        let groups = filterOptions?.ectsOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByEctsInput = (
        <div>
            <label>ECTS</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.ectsOptions ?? []} style={{ width: "130px" }} 
                onChange={(ectsOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, ectsOptions: ectsOptions})}}
                options={getEctsOptions()}>
            </Select>
        </div>
    );
    
    const getExternalEvaluationOptions = () => {
        let options: any = [];

        let groups = filterOptions?.externalEvaluationOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByExternalEvaluationInput = (
        <div>
            <label>Data ultimei evaluari</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.externalEvaluationOptions ?? []} style={{ width: "150px" }} 
                onChange={(externalEvaluationOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, externalEvaluationOptions: externalEvaluationOptions})}}
                options={getExternalEvaluationOptions()}>
            </Select>
        </div>
    );

    const getAccreditationTypeOptions = () => {
        let options: any = [];

        let groups = filterOptions?.accreditationTypeOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: mapAccreditationTypeShortToString(value),
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByAccreditationTypeInput = (
        <div>
            <label>Acreditare</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.accreditationTypeOptions ?? []} style={{ width: "130px" }} 
                onChange={(accreditationTypeOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, accreditationTypeOptions: accreditationTypeOptions})}}
                options={getAccreditationTypeOptions()}>
            </Select>
        </div>
    );

    const FilterByEurAceCertificationInput = (
        <div>
            <label>Certificare EUR-ACE</label>
            <Select
                value={filters.eurAceCertificationFilter}
                style={{ width: "140px" }}
                onChange={(eurAceCertificationFilter: 'all' | 'yes' | 'no') => {
                    handleTablePageChange(1, filters.pageSize);
                    setFilters({ ...filters, eurAceCertificationFilter });
                }}
                options={[
                    { label: 'Toate', value: 'all' },
                    { label: 'Da', value: 'yes' },
                    { label: 'Nu', value: 'no' },
                ]}
            />
        </div>
    );


    const getCapacityOptions = () => {
        let options: any = [];

        let groups = filterOptions?.capacityOptions;
        
        if (groups != null)
        {
            for (let value of [...groups].sort((a, b) => a - b)) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByCapacityInput = (
        <div>
            <label>Capacitate</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.capacityOptions ?? []} style={{ width: "130px" }} 
                onChange={(capacityOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, capacityOptions: capacityOptions})}}
                options={getCapacityOptions()}>
            </Select>
        </div>
    );

    const getMasterCategoryOptions = () => {
        let options: any = [];

        let groups = filterOptions?.masterCategoryOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByMasterCategoryInput = (
        <div>
            <label>Categorie Masterat</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.masterCategoryOptions ?? []} style={{ width: "130px" }} 
                onChange={(masterCategoryOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, masterCategoryOptions: masterCategoryOptions})}}
                options={getMasterCategoryOptions()}>
            </Select>
        </div>
    );


    const getAncNoticeDateOptions = () => {
        let options: any = [];

        let groups = filterOptions?.ancNoticeDateOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByAncNoticeDateInput = (
        <div>
            <label>Data Aviz ANC</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.ancNoticeDateOptions ?? []} style={{ width: "180px" }} 
                onChange={(ancNoticeDateOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, ancNoticeDateOptions: ancNoticeDateOptions})}}
                options={getAncNoticeDateOptions()}>
            </Select>
        </div>
    );

    const getAncExpirationDateOptions = () => {
        let options: any = [];

        let groups = filterOptions?.ancExpirationDateOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByAncExpirationDateInput = (
        <div>
            <label>Data Expirare ANC</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.ancExpirationDateOptions ?? []} style={{ width: "180px" }} 
                onChange={(ancExpirationDateOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, ancExpirationDateOptions: ancExpirationDateOptions})}}
                options={getAncExpirationDateOptions()}>
            </Select>
        </div>
    );

    const getarAcisExpirationDateOptions = () => {
        let options: any = [];

        let groups = filterOptions?.aracisExpirationDateOptions;
        
        if (groups != null)
        {
            for (let  value of groups) {
                options.push({
                    label: value,
                    value: value
                });
            } 
        }
        return options;
    }

    const FilterByAracisExpirationDateInput = (
        <div>
            <label>Data Expirare ARACIS</label>
            <Select mode="multiple" maxTagCount="responsive" allowClear placeholder="Toate" value={filters.aracisExpirationDateOptions ?? []} style={{ width: "180px" }} 
                onChange={(aracisExpirationDateOptions) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, aracisExpirationDateOptions: aracisExpirationDateOptions})}}
                options={getarAcisExpirationDateOptions()}>
            </Select>
        </div>
    );
    
    const FilterByFacultyInput = (
        <div>
            <label>{isDoctorate ? 'Școala Doctorală' : 'Facultate'}</label>
            <Select
                mode="multiple"
                allowClear
                showSearch
                maxTagCount={1}
                maxTagTextLength={30}
                maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
                filterOption={(input, option) => {
                    const label = typeof (option as any)?.children === 'string'
                        ? (option as any).children
                        : String((option as any)?.children ?? '');
                    return label.toLowerCase().includes(input.toLowerCase());
                }}
                value={filters.searchTermFaculty}
                style={{ width: "370px" }} 
                placeholder="Toate"
                onChange={(searchTermFaculty) => {
                    handleTablePageChange(1, filters.pageSize)
                    setFilters({...filters, searchTermFaculty: searchTermFaculty || []})}}>
                {(faculties?.filter((f: any) => isDoctorate ? f.isDoctoralSchool : !f.isDoctoralSchool) ?? [])
                    .map((option: any, index: number) => (
                    <Option key={option.id} value={option.id}>
                        {index + (filters.searchTermUniversity === '00000000-0000-0000-0012-000000000101' ? 18 : 1)}. {option.name}
                    </Option>
                ))}
            </Select>
        </div>
    );
    
    const FilterByUniversityInput = (
        <div>
            <label><br></br></label>
            <Select defaultValue={filters.searchTermUniversity} style={{ width: "80px" }} 
                    onChange={(searchTermUniversity) => {
                        handleTablePageChange(1, filters.pageSize)
                        setFilters({...filters, searchTermUniversity: searchTermUniversity, searchTermFaculty: []})}}>
                { [<Option key={"00000000-0000-0000-0000-000000000000"}>{"Toate"}</Option>].concat(
                universities?.map((option: any,) => (
                    <Option key={option.id}>{option.shortName}</Option>
                )) ?? [] )}
            </Select>
        </div>
    );

    const statusColors = (status: StudyPlanStatusEnum) => { // de modficat cu enumul corect 
            switch (status) {
                case StudyPlanStatusEnum.Draft: return 'red';
                case StudyPlanStatusEnum.PendingDeanValidation: return 'orange';
                case StudyPlanStatusEnum.PendingQaValidation: return 'gold';
                case StudyPlanStatusEnum.PendingFacultyCouncilApproval: return 'magenta';
                case StudyPlanStatusEnum.Approved: return 'green';
                case StudyPlanStatusEnum.All: return 'volcano';
            }
        };
    
        const statusToString = (status: StudyPlanStatusEnum) => { // de modficat cu enumul corect
            switch (status) {
                case StudyPlanStatusEnum.Draft: return 'Draft';
                case StudyPlanStatusEnum.PendingDeanValidation: return 'Validare Decan';
                case StudyPlanStatusEnum.PendingQaValidation: return 'Validare Calitate';
                case StudyPlanStatusEnum.PendingFacultyCouncilApproval: return 'Validare Departament';
                case StudyPlanStatusEnum.Approved: return 'Aprobat';
                case StudyPlanStatusEnum.All: return 'All';
            }
        };


    const columns : ColumnsType<any> = [
        {
            title: FilterByUniversityInput,
            dataIndex: 'universityName',
            key: 'universityName',
            width: 100,
        },
        {
            title: FilterByFacultyInput,
            dataIndex: 'facultyName',
            key: 'facultyName',
            width: 400,
        },
        {
            title: FilterByDomainInput,
            dataIndex: 'domainName',
            key: 'domainName',
            render: (text: string, record: any, index: number) => {
                return text;
            },
            width: 400,
        },
        ...(isDoctorate ? [] : [{
            title: FilterByNameInput,
            dataIndex: 'name',
            render: (text: string, record: any, index: number) => {
                return text;
            },
            width: 300,
        }]),
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (_: any, record: any) => {
                const status = record.status ?? StudyPlanStatusEnum.Draft;
    
                return (
                <Tag color={statusColors(status)} key={status}>
                    {statusToString(status)}
                </Tag>
                );
            },
            width: 150,
        },
        ...(isDoctorate ? [] : [{
            title: FilterByLanguageInput,
            key: 'language',
            dataIndex: 'language',
            render: (text: string, record: any, index: number) => {
                return mapStudyProgramLangToString(record.language);
            },
            width: 200,
        }]),
        {
            title: FilterByEctsInput,
            key: 'numberECTS',
            dataIndex: 'numberECTS',
            width: 150,
        },
        {
            title: FilterByAccreditationTypeInput,
            key: 'accreditationType',
            dataIndex: 'accreditationType',
            width: 150,
            render:(value: AccreditationTypeEnum) => 
            { 
                return mapAccreditationTypeToString(value);
            },
        },
        ...(isDoctorate ? [] : [{
            title: FilterByEurAceCertificationInput,
            key: 'isEurAceCertified',
            dataIndex: 'isEurAceCertified',
            width: 170,
            render: (value: boolean) => value ? 'Da' : 'Nu',
        }]),
        ...(isDoctorate ? [] : [{
            title: FilterByCapacityInput,
            key: 'capacity',
            dataIndex: 'capacity',
            width: 150,
        }]),
        {
            title: 'Domeniu educational ISCED 2013',
            key: 'educaionalDomainISCED',
            dataIndex: 'educaionalDomainISCED',
            width: 250,
        },
        ...(isDoctorate ? [] : [{
            title: FilterByCodCorInput,
            key: 'codCOR',
            dataIndex: 'codCOR',
            width: 280,
        },
        {
            title: FilterByOcuppationInput,
            key: 'ocupationName',
            dataIndex: 'ocupationName',
            width: 400,
        }]),
        
        studyCycle === StudyCycleEnum.Master ? {
            title: FilterByMasterCategoryInput,
            key: 'masterCategory',
            dataIndex: 'masterCategory',
            render:(value: MasterCategoryEnum) => 
            { 
                switch(value) {
                case MasterCategoryEnum.NotApplied: return "Nu se aplica"
                case MasterCategoryEnum.Professional: return "Profesional"
                case MasterCategoryEnum.Research: return "De Cercetare"
                case MasterCategoryEnum.Teaching: return "Didactic"
            } },
            width:  250,
        } : {},
        ...(isDoctorate ? [] : [{
            title: 'Nr. Aviz ANC',
            key: 'ancNoticeNumber',
            dataIndex: 'ancNoticeNumber',
            width: 150,
        }]),
        ...(isDoctorate ? [] : [{
            title: FilterByAncNoticeDateInput,
            key: 'ancNoticeDate',
            dataIndex: 'ancNoticeDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") },
            width: 200,
        }]),
        ...(isDoctorate ? [] : [{
            title: FilterByAncExpirationDateInput,
            key: 'ancExpireDate',
            dataIndex: 'ancExpireDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") },
            width: 200,
        }]),
        {
            title: FilterByAracisExpirationDateInput,
            key: 'aracisExpireDate',
            dataIndex: 'aracisExpireDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") },
            width: 200,
        },
        {
            title: 'Istoric Evaluari Externe',
            key: 'externalEvaluationHistory',
            dataIndex: 'externalEvaluationHistory',
            width: 150,
        },
        {
            title: FilterByExternalEvaluationInput,
            key: 'lastEvaluationDate',
            dataIndex: 'lastEvaluationDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") },
            width: 190,
        },
        {
            title: isDoctorate ? 'Responsabil domeniu' : 'Responsabil program',
            key: 'programSupervisor',
            dataIndex: 'programSupervisor',
            width: 250,
        },
        {
            title: 'Acțiuni',
            fixed: 'right',
            width: '80px',
            render: (text: string, record: any, index: number) =>
                <Dropdown placement="bottomLeft"
                    menu={{
                        onClick: (e) => onMenuClick(e, record),
                        items: getActions(record)
                    }}
                >
                    <FontAwesomeIcon icon={solid('bars')} style={{ cursor: 'pointer', height: "1rem", padding: "6px 0px", marginLeft: "13px"}}/>
                </Dropdown>
        }
    ];

    // Optional "detail" columns the user can hide/show via the "Coloane" button. Identifying
    // columns (university/faculty/domain/name/status) and the actions column always stay visible,
    // so they're intentionally left out of this list. Keys must match the `key`/`dataIndex` used
    // by the corresponding entry in `columns` above.
    const toggleableColumns: { key: string, label: string }[] = [
        ...(isDoctorate ? [] : [{ key: 'language', label: 'Limba de predare' }]),
        { key: 'numberECTS', label: 'ECTS' },
        { key: 'accreditationType', label: 'Acreditare' },
        ...(isDoctorate ? [] : [{ key: 'isEurAceCertified', label: 'Certificare EUR-ACE' }]),
        ...(isDoctorate ? [] : [{ key: 'capacity', label: 'Capacitate' }]),
        { key: 'educaionalDomainISCED', label: 'Domeniu educational ISCED 2013' },
        ...(isDoctorate ? [] : [{ key: 'codCOR', label: 'Cod COR/ESCO' }, { key: 'ocupationName', label: 'Denumire ocupație' }]),
        ...(studyCycle === StudyCycleEnum.Master ? [{ key: 'masterCategory', label: 'Categorie Masterat' }] : []),
        ...(isDoctorate ? [] : [
            { key: 'ancNoticeNumber', label: 'Nr. Aviz ANC' },
            { key: 'ancNoticeDate', label: 'Data Aviz ANC' },
            { key: 'ancExpireDate', label: 'Data Expirare ANC' },
        ]),
        { key: 'aracisExpireDate', label: 'Data Expirare ARACIS' },
        { key: 'externalEvaluationHistory', label: 'Istoric Evaluări Externe' },
        { key: 'lastEvaluationDate', label: 'Data ultimei evaluări' },
        { key: 'programSupervisor', label: isDoctorate ? 'Responsabil domeniu' : 'Responsabil program' },
    ];

    const visibleColumns = columns.filter((col: any) => {
        const colKey = col?.key ?? col?.dataIndex;
        if (colKey === undefined || colKey === '') return true;
        return columnVisibility[colKey] !== false;
    });

    const ColumnsButton = (
        <Popover
            trigger="click"
            placement="bottomRight"
            content={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 400, overflowY: 'auto', minWidth: 220 }}>
                    {toggleableColumns.map(col => (
                        // AntD's default `.ant-checkbox-wrapper + .ant-checkbox-wrapper { margin-left: 8px }`
                        // assumes a horizontal layout and misaligns every item after the first in this
                        // vertical list. Wrapping each in its own div keeps them from being direct
                        // `.ant-checkbox-wrapper` siblings, so that rule no longer matches.
                        <div key={col.key}>
                            <Checkbox
                                checked={columnVisibility[col.key] !== false}
                                onChange={(e) => setColumnVisibility(prev => ({ ...prev, [col.key]: e.target.checked }))}
                            >
                                {col.label}
                            </Checkbox>
                        </div>
                    ))}
                </div>
            }
        >
            <Button icon={<SettingOutlined />} style={{ marginRight: 10 }}>
                Coloane
            </Button>
        </Popover>
    );


    const reportColumns: ColumnType<any>[] = [
        {
            title: 'Facultatea',
            key: 'facultyName',
        },
        {
            title: 'Domeniu',
            key: 'domainName',
        },
        {
            title: 'Specializarea/Program de studii universitare de licenţă  şi limba de predare',
            key: 'name',
        },
        {
            title: 'Nr. ECTS',
            key: 'numberECTS',
        },
        {
            title: 'Acreditare (A)/ Autorizare de funcționare provizorie (AP)',
            key: 'accreditationType',
        },
        {
            title: 'Capacitatea de școlarizare',
            key: 'capacity',
        },
        {
            title: 'Domeniu educational ISCED 2013',
            key: 'educaionalDomainISCED',
        },
        {
            title: 'Cod COR/ESCO',
            key: 'codCOR',
        },
        {
            title: 'Denumire ocupatie',
            key: 'ocupationName',
        },
        studyCycle === StudyCycleEnum.Master ? {
            title: 'Categorie Masterat',
            key: 'masterCategoryDescription',
        } : {title: '',
        key: '',},
        {
            title: 'Nr. Aviz ANC',
            key: 'ancNoticeNumber',
            // render:(text: string, record: any, index: number) => {
            //     return record.ancNoticeNumber + "/" + record.ancNoticeDate?.toLocaleDateString("ro-RO");
            // }
        },
        {
            title: 'Data Aviz ANC',
            key: 'ancNoticeDate',
            // render:(date: Date) => { return date?.toLocaleDateString("ro-RO") }
        },
        {
            title: 'Data Expirare ANC',
            key: 'ancExpireDate',
            // render:(date: Date) => { return date?.toLocaleDateString("ro-RO") }
        },
        {
            title: 'Istoric Evaluari Externe',
            key: 'externalEvaluationHistory',
        },
        {
            title: 'Data ultimei evaluari',
            key: 'lastEvaluationDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") }
        },
        {
            title: 'Data Aviz ARACIS',
            key: 'aracisNoticeDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") }
        },
        {
            title: 'Data Expirare ARACIS',
            key: 'aracisExpireDate',
            render:(date: Date) => { return date?.toLocaleDateString("ro-RO") }
        },
        
        
        {
            title: 'Responsabil program',
            key: 'programSupervisor',
            dataIndex: 'programSupervisor',
        },
    ];

    const duplicateReportColumns: ColumnType<any>[] = [
        {
            title: isDoctorate ? 'Școala doctorală' : 'Facultatea',
            key: 'facultyName',
        },
        {
            title: 'Domeniu',
            key: 'domainName',
        },
        {
            title: isDoctorate ? 'Domeniu doctoral' : 'Program de studii',
            key: 'programName',
        },
        {
            title: 'Plan cadru',
            key: 'planCadru',
        },
        {
            title: 'Denumire disciplină',
            key: 'disciplineName',
        },
        {
            title: 'Denumire EN',
            key: 'disciplineNameEN',
        },
        {
            title: 'Cod disciplină',
            key: 'disciplineCode',
        },
        {
            title: 'An studiu',
            key: 'studyYear',
        },
        {
            title: 'Semestru',
            key: 'semester',
        },
        {
            title: 'Nr. potențiale duplicate',
            key: 'duplicateCount',
        },
        {
            title: 'Discipline similare',
            key: 'matchingDisciplines',
        },
    ];

    const missingTitularReportColumns: ColumnType<any>[] = [
        {
            title: isDoctorate ? 'Școala doctorală' : 'Facultatea',
            key: 'facultyName',
        },
        {
            title: 'Domeniu',
            key: 'domainName',
        },
        {
            title: isDoctorate ? 'Domeniu doctoral' : 'Program de studii',
            key: 'programName',
        },
        {
            title: 'Plan cadru',
            key: 'planCadru',
        },
        {
            title: 'Denumire disciplină',
            key: 'disciplineName',
        },
        {
            title: 'Cod disciplină',
            key: 'disciplineCode',
        },
        {
            title: 'An studiu',
            key: 'studyYear',
        },
        {
            title: 'Semestru',
            key: 'semester',
        },
    ];

    const nonApprovedSubjectsReportColumns: ColumnType<any>[] = [
        ...missingTitularReportColumns,
        {
            title: 'Status',
            key: 'status',
        },
    ];

    const [progress, setProgress] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);

    const openErrorNotification = (error: string, message: string) => {
        openNotification(
            error,
            message,
            NOTIFICATION_TYPES.ERROR
        );
    }

    async function downloadAll<T>(getCall: PageCallBack<T>, setProgress: (progress: number) => void, onError: () => void) {
        let data = new Array<T>();
        let hasData = true;
        let pageIndex = 1;
        setProgress(0);

        do {
            const page = await getCall(pageIndex, 100).catch(onError);

            ++pageIndex;

            if (page?.data === undefined || page?.data === null) {
                onError();

                return;
            }

            data = data.concat(page.data);

            if ((pageIndex - 1) * 100 >= (page.totalCount ?? 0)) {
                hasData = false;
            }

            setProgress(hasData ? (Math.round((pageIndex - 1) * 100 / (page.totalCount ?? 1) * 100)) : 100)
        } while (hasData);

        return data;
    }

    const uploadExcel = async (file: File) => {
        setUploading(true);
        try {
            await modalProps.uploadHandler(modalProps.studyProgramId, file);
            openNotification('Succes', 'Import efectuat cu succes!', NOTIFICATION_TYPES.SUCCESS);
            invalidateQuery();
            handleClose();
        } catch (e) {
            console.log('e', e); 
            await showImportError(e); 
        } finally {
            setUploading(false);
        }
        };


    const downloadReport = async () => {
        setDownloading(true);
        let fileName = 'Raport Evaluări - ' + getStudyCycleLabel() + ' - ' + (new Date()).toLocaleDateString("ro-RO");
        return downloadExcel(fileName, reportColumns.map(e => { return { header: e.title, key: e.key?.toString() ?? '' } }),
            await downloadAll((page, size) => getStudyPrograms(buildReportQueryParams(page, size)), (value) => {
                setDownloadError(false);
                setProgress(value)
            }, () => {
                setDownloadError(true);
                openErrorNotification("Eroare!", "Raportul nu a putut fi descărcat cu succes!")
            }));
    }

    const downloadDuplicateDisciplinesReport = async () => {
        setDownloading(true);
        setDownloadError(false);
        setProgress(0);
        const fileName = 'Raport Discipline Duplicate - ' + getStudyCycleLabel() + ' - ' + (new Date()).toLocaleDateString("ro-RO");

        try {
            const rows = await getDuplicateDisciplinesReport(buildReportQueryParams());
            setProgress(100);
            await downloadExcel(
                fileName,
                duplicateReportColumns.map(e => ({ header: e.title, key: e.key?.toString() ?? '' })),
                rows
            );
        } catch {
            setDownloadError(true);
            openErrorNotification("Eroare!", "Raportul de discipline duplicate nu a putut fi descărcat cu succes!");
        } finally {
            setDownloading(false);
        }
    }

    const learningOutcomesReportColumns = [
        { header: 'Nr. crt.', key: 'orderNumber' },
        { header: 'Tip rezultate', key: 'outcomeType' },
        { header: 'Rezultat', key: 'description', width: 100 },
        { header: 'Indicator rezultat', key: 'identificator' },
    ];

    const downloadLearningOutcomesReport = async () => {
        setDownloading(true);
        setDownloadError(false);
        setProgress(0);
        const fileName = 'Raport Rezultate Invatare - ' + getStudyCycleLabel() + ' - ' + (new Date()).toLocaleDateString("ro-RO");

        try {
            const programs = await getLearningOutcomesReport(buildReportQueryParams());
            setProgress(100);

            if (programs.length === 0) {
                openNotification('Atenție!', 'Nu există programe cu plan cadru pentru filtrele selectate.', NOTIFICATION_TYPES.INFO);
                return;
            }

            // o foaie per program, in ordinea din tabel
            await downloadExcelWorkbook(
                fileName,
                programs.map(program => ({
                    name: program.programName ?? '',
                    // numele foii se taie la 31 de caractere, deci programele cu nume asemanatoare
                    // ajung la fel in tab-uri; numele complet se scrie deasupra tabelului
                    headerLines: [
                        program.programName ?? '',
                        [program.facultyName, program.planCadru ? 'Plan cadru ' + program.planCadru : '']
                            .filter(Boolean)
                            .join(' — ')
                    ].filter(Boolean),
                    columns: learningOutcomesReportColumns,
                    rows: program.rows ?? []
                }))
            );
        } catch {
            setDownloadError(true);
            openErrorNotification("Eroare!", "Raportul privind rezultatele învățării nu a putut fi descărcat cu succes!");
        } finally {
            setDownloading(false);
        }
    }

    const downloadMissingTitularsReport = async () => {
        setDownloading(true);
        setDownloadError(false);
        setProgress(0);
        const fileName = 'Raport Discipline Fara Titular - ' + getStudyCycleLabel() + ' - ' + (new Date()).toLocaleDateString("ro-RO");

        try {
            const rows = await getMissingTitularsReport(buildReportQueryParams());
            setProgress(100);
            await downloadExcel(
                fileName,
                missingTitularReportColumns.map(e => ({ header: e.title, key: e.key?.toString() ?? '' })),
                rows
            );
        } catch {
            setDownloadError(true);
            openErrorNotification("Eroare!", "Raportul de discipline fără titular nu a putut fi descărcat cu succes!");
        } finally {
            setDownloading(false);
        }
    }

    const downloadNonApprovedSubjectsReport = async () => {
        setDownloading(true);
        setDownloadError(false);
        setProgress(0);
        const fileName = 'Raport Discipline Neaprobate - ' + getStudyCycleLabel() + ' - ' + (new Date()).toLocaleDateString("ro-RO");

        try {
            const rows = await getNonApprovedSubjectsReport(buildReportQueryParams());
            setProgress(100);
            await downloadExcel(
                fileName,
                nonApprovedSubjectsReportColumns.map(e => ({ header: e.title, key: e.key?.toString() ?? '' })),
                rows
            );
        } catch {
            setDownloadError(true);
            openErrorNotification("Eroare!", "Raportul de discipline neaprobate nu a putut fi descărcat cu succes!");
        } finally {
            setDownloading(false);
        }
    }

    const reportsMenuItems: MenuProps['items'] = [
        {
            key: 'evaluationReport',
            label: 'Descărcare raport programe',
        },
        {
            key: 'duplicateDisciplinesReport',
            label: 'Raport discipline duplicate',
        },
        {
            key: 'missingTitularsReport',
            label: 'Raport discipline fără titular',
        },
        {
            key: 'nonApprovedSubjectsReport',
            label: 'Raport discipline neaprobate',
        },
        {
            key: 'learningOutcomesReport',
            label: 'Raport rezultate învățare',
        },
    ];

    const onReportsMenuClick = (e: { key: string }) => {
        switch (e.key) {
            case 'evaluationReport':
                downloadReport();
                break;
            case 'duplicateDisciplinesReport':
                downloadDuplicateDisciplinesReport();
                break;
            case 'missingTitularsReport':
                downloadMissingTitularsReport();
                break;
            case 'nonApprovedSubjectsReport':
                downloadNonApprovedSubjectsReport();
                break;
            case 'learningOutcomesReport':
                downloadLearningOutcomesReport();
                break;
        }
    }

    return <div className={styles.container} style={{ display: "flex", flexDirection: "column", marginTop: "0%" }}>
        <Layout>
            <Modal
                open={modalPropsDelete.isModalVisible}
                onOk={handleOkDelete}
                onCancel={handleCloseDelete}
                title="Stergere"
                width={"50%"}
                okText={t('account.confirm')}
                cancelText={t('account.cancel')}
                cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                okButtonProps={{ style: { background: theme.secondColor, border: theme.secondColor, borderRadius: "10px" } }}
                >
                    <div>
                        <Typography>Sigur doriti stergerea propunerii?</Typography>
                    </div>
            </Modal>
            <Modal
                open={modalProps.isModalVisible}
                closable={false}
                onOk={handleOk}
                onCancel={handleClose}
                title="Încărcare fișier"
                width={"50%"}
                cancelText='Anulare'
                cancelButtonProps={{ style: { background: theme.green, border: theme.green, color: theme.white, borderRadius: "10px" } }}
                okButtonProps={{ style: { display: 'none' } }}
                >
                <div>
                    <Dragger
                    multiple={false}
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    showUploadList={false}
                    beforeUpload={(uploaded) => {
                        const ext = String(modalProps.fileExtension || 'xlsx').toLowerCase().replace(/^\./,'');
                        const isOk = uploaded.name.toLowerCase().endsWith(`.${ext}`);
                        if (!isOk) { wrongFile(); return Upload.LIST_IGNORE; }

                        uploadExcel(uploaded as File).catch(uploadFailed);
                        return false;
                    }}
                    disabled={uploading}
                    >
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined style={{ color: theme.green }} />
                    </p>
                    <p className="ant-upload-text">
                        Dă click aici sau trage fișierul pe care vrei să îl încarci în acest chenar
                    </p>
                    <p className="ant-upload-hint">
                        Extensii acceptate: {modalProps.fileExtension}
                    </p>
                    </Dragger>
                </div>
</Modal>
            {downloading && <div onClick={() => setDownloading(false)} style={{cursor: "pointer"}}>
                <Progress percent={progress} status={downloadError ? "exception" : undefined} />
            </div>}
            <div style={{ display: "flex", justifyContent: "flex-end", margin: "16px 0" }}>
                <Pagination 
                    total={data?.totalCount}
                    current={currentPage ?? 1}
                    pageSize={filters.pageSize ?? 10}
                    pageSizeOptions={["10", "20", "50"]}
                    defaultPageSize={10}
                    showSizeChanger={true}
                    onChange={handleTablePageChange}
                    showTotal={(total: number, range: [number, number]) => {
                        return <>
                                {canManageDoctorate && <Dropdown menu={{ items: reportsMenuItems, onClick: onReportsMenuClick }} placement="bottomLeft">
                                    <Button type="primary" icon={<DownloadOutlined />} style={{marginRight: 10}}>
                                        Rapoarte
                                    </Button>
                                </Dropdown>}
                                {canManageDoctorate && <Tooltip title={isDoctorate ? "Adaugă domeniu" : "Adaugă program"}>
                                    <Link to={{ pathname: '/' + studyCycle + '/adaugare-program'}} >
                                        <Button style={{marginRight: 10}} type="primary" icon={<PlusOutlined />}>
                                            {isDoctorate ? 'Adaugă domeniu' : 'Adaugă program'}
                                        </Button>
                                    </Link>
                                </Tooltip>}
                                {ColumnsButton}
                                <Button type="default">
                                    Total programe: {data?.totalCount}
                                </Button>
                            </>
                    }}
                    hideOnSinglePage={ false}
                    locale={{ items_per_page: t('configuration.pagination')}}
                >
                </Pagination>
            </div>
            <div>
                <Table
                    className={styles.usersTable}
                    dataSource={data?.data || []}
                    scroll={{ x: 1500, y: 'calc(100vh - 380px)'  }}
                    locale={{
                        emptyText: (
                            <span>
                                <h3>{isDoctorate ? 'Nu există domenii adăugate! Apasă butonul de mai jos pentru a adăuga un domeniu nou!' : 'Nu există programe adăugate! Apasă butonul de mai jos pentru a adăuga un program nou!'}</h3>
                                {canManageDoctorate && <Tooltip title={isDoctorate ? "Adaugă domeniu" : "Adaugă program"}>
                                    <Link to={{ pathname: '/' + studyCycle + '/adaugare-program'}} >
                                        <Button type="primary" icon={<PlusOutlined />}>
                                            {isDoctorate ? 'Adaugă domeniu' : 'Adaugă program'}
                                        </Button>
                                    </Link>
                                </Tooltip>}
                            </span>
                        )
                    }}
                    pagination={false}
                    columns={visibleColumns}
                    rowKey={record => record.id ?? ''}
                    loading={isLoading}
                    sticky={true}
                />
            </div>

        </Layout>
    </div>
}

export default StudyPrograms;
